import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import type { RuleConfig } from '@t212/shared';
import { api } from '@/lib/api';
import { RuleParamInput } from '@/components/RuleParamInput';
import { LoadingSkeleton } from '@/components/states/LoadingSkeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/rules')({
  component: RulesPage,
});

const sevIcon = {
  block: ShieldAlert,
  warn: ShieldQuestion,
  info: ShieldCheck,
} as const;

const sevColor = {
  block: 'text-rose-400',
  warn: 'text-amber-400',
  info: 'text-indigo-300',
} as const;

function RulesPage() {
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ['rules'], queryFn: api.rules });
  const violations = useQuery({ queryKey: ['violations'], queryFn: api.violations });

  const patch = useMutation({
    mutationFn: (input: { id: string; body: Partial<RuleConfig> }) =>
      api.patchRule(input.id, input.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      qc.invalidateQueries({ queryKey: ['violations'] });
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">Rules</h1>
        <p className="text-[12px] text-zinc-400 mt-0.5">
          Risk guard rails evaluated on every poll. Toggle, edit thresholds, see
          which positions are tripping each rule.
        </p>
      </div>

      {rules.isError && (
        <ErrorState
          title="Couldn't load rules"
          hint={rules.error instanceof Error ? rules.error.message : undefined}
          onRetry={() => rules.refetch()}
        />
      )}

      {rules.isLoading && (
        <div className="space-y-3">
          <LoadingSkeleton variant="card" />
          <LoadingSkeleton variant="card" />
          <LoadingSkeleton variant="card" />
        </div>
      )}

      <ul className="space-y-3">
        {rules.data?.map((r) => {
          const Icon = sevIcon[r.severity];
          const myViolations = (violations.data?.violations ?? []).filter(
            (v) => v.ruleId === r.id,
          );
          return (
            <li
              key={r.id}
              className="rounded-xl border border-zinc-800 bg-zinc-950 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Icon className={cn('size-5 mt-0.5', sevColor[r.severity])} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                      {r.name}
                      <code className="font-mono text-[10px] text-zinc-400 bg-zinc-900 px-1.5 py-0.5 rounded">
                        {r.id}
                      </code>
                      <span
                        className={cn(
                          'text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border',
                          r.severity === 'block'
                            ? 'border-rose-500/40 text-rose-300'
                            : r.severity === 'warn'
                              ? 'border-amber-500/40 text-amber-300'
                              : 'border-indigo-500/40 text-indigo-300',
                        )}
                      >
                        {r.severity}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 text-pretty">{r.description}</p>

                    {Object.keys(r.params).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        {Object.entries(r.params)
                          .filter(([, v]) => typeof v === 'number')
                          .map(([k, v]) => {
                            const def = r.defaultParams[k];
                            return (
                              <RuleParamInput
                                key={k}
                                name={k}
                                value={typeof v === 'number' ? v : 0}
                                defaultValue={typeof def === 'number' ? def : 0}
                                onSave={(next) =>
                                  patch.mutateAsync({
                                    id: r.id,
                                    body: { params: { ...r.params, [k]: next } },
                                  })
                                }
                              />
                            );
                          })}
                      </div>
                    )}

                    {myViolations.length > 0 && (
                      <div className="mt-3 text-xs text-zinc-400">
                        Tripped by:{' '}
                        {myViolations.map((v, i) => (
                          <span key={i}>
                            {i > 0 && <span className="text-zinc-700"> · </span>}
                            <span className="text-zinc-300">{v.ticker ?? '—'}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={r.enabled}
                  aria-label={`Toggle rule ${r.name}`}
                  onClick={() =>
                    patch.mutate(
                      { id: r.id, body: { enabled: !r.enabled } },
                      {
                        onSuccess: () =>
                          toast.success(`${r.name} ${!r.enabled ? 'enabled' : 'disabled'}`),
                      },
                    )
                  }
                  className={cn(
                    'shrink-0 inline-flex items-center w-10 h-6 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950',
                    r.enabled
                      ? 'bg-indigo-500/60 border-indigo-500 justify-end'
                      : 'bg-zinc-800 border-zinc-700 justify-start',
                  )}
                >
                  <span className="size-5 rounded-full bg-zinc-100 mx-0.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
